/* eslint-disable jsx-a11y/anchor-has-content */
/* eslint-disable jsx-a11y/tabindex-no-positive */
import { reportError } from "@cabocheck/shared";
import type { MedicalTestProfile, PartnerAppointmentType } from "@cabocheck/shared";
import { Trans, useTranslation } from "react-i18next";
import { z } from "zod";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import usePlacesService from "react-google-autocomplete/lib/usePlacesAutocompleteService";
import { ComboBoxFormInput } from "~/components/form/ComboBoxFormInput";
import { DateFormInput } from "~/components/form/DateFormInput";
import { ReactHookForm } from "~/components/form/Form";
import { SelectFormInput } from "~/components/form/SelectFormInput";
import { TextFormInput } from "~/components/form/TextFormInput";
import { FormMessage } from "~/components/ui/form";
import { FormControl, FormItem, FormLabel } from "~/components/ui/form";
import { FormField } from "~/components/ui/form";
import { Card, CardContent } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { AttachmentBlock } from "~/components/AttachmentList";
import { Button } from "~/components/ui/button";
import { DownloadIcon } from "lucide-react";
import type { InsuranceCompany } from "@cabocheck/shared";

export function PatientDetails({
  onSubmit,
  appointmentType,
  medicalTestProfiles,
  medicalTestProfileGuid,
  bookingError,
}: Props) {
  const { t } = useTranslation();
  const isRequestMode = appointmentType?.booking_mode === "request";
  const hasVaccination = Boolean(appointmentType?.vaccinations?.length);
  const hasLinkedProduct = Boolean(appointmentType?.product_guid);

  const { data: insuranceCompaniesData } = useQuery({
    queryKey: ["insurance_companies"],
    queryFn: async () => {
      const res = await fetch("/api/v1/insurance_companies");
      const json = await res.json();
      return json as InsuranceCompany[];
    },
    enabled: hasVaccination,
  });

  const insuranceCompanies = (insuranceCompaniesData ?? []).map((ic) => ({
    label: ic.name,
    value: ic.insurance_company_guid,
  }));

  const PatientSchema = usePatientSchema(hasVaccination);

  const testProfileField =
    appointmentType?.can_select_test_profile && !hasVaccination && !hasLinkedProduct ? (
      <ComboBoxFormInput
        tabIndex={7}
        name="medicalTestProfileGuid"
        label={t("Testprofil")}
        options={medicalTestProfiles
          .filter((profile) => profile.enabled)
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((profile) => ({
            label: profile.name,
            value: profile.medical_test_profile_guid,
          }))}
      />
    ) : null;

  const add_documents_block =
    appointmentType?.can_add_documents || appointmentType?.attachments ? (
      <Card className="p-0">
        <CardContent className="space-y-4">
          {appointmentType?.attachments ? (
            <div className="mt-2">
              <div className="text-sm font-medium text-gray-800">{t("Anhänge")}</div>
              {appointmentType?.attachments_descriptions ? (
                <div className="mt-2 text-sm text-gray-700">{appointmentType.attachments_descriptions}</div>
              ) : null}
              <div className="mt-1 flex flex-wrap gap-2">
                {appointmentType.attachments
                  .split(",")
                  .map((u) => u.trim())
                  .filter((u) => u.length > 0)
                  .map((url, idx) => (
                    <AttachmentBlock
                      key={url}
                      url={url}
                      index={idx}
                      button={
                        <a href={url} target="_blank" rel="noopener noreferrer">
                          <Button size="icon">
                            <DownloadIcon size={16} />
                          </Button>
                        </a>
                      }
                    />
                  ))}
              </div>
            </div>
          ) : null}
          {appointmentType?.can_add_documents ? (
            <>
              {appointmentType?.add_document_description ? (
                <div className="mt-2 text-sm text-gray-700">{appointmentType.add_document_description}</div>
              ) : null}
              <FormField
                name="files"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <FormLabel>{t("Dateien")}</FormLabel>
                      <FormControl>
                        <Input
                          type="file"
                          onChange={(e) => field.onChange(e.target.files)}
                          multiple
                          accept="image/*,application/pdf"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
            </>
          ) : null}
        </CardContent>
      </Card>
    ) : null;

  return (
    <div className="p-8">
      {bookingError ? (
        <div className="mb-6 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">{bookingError}</div>
      ) : null}
      {isRequestMode && appointmentType?.request_hint_text ? (
        <div className="mb-6 flex gap-2 rounded-md bg-blue-50 p-3 text-sm text-blue-900">
          <p>{appointmentType.request_hint_text}</p>
        </div>
      ) : null}
      <ReactHookForm
        initialValues={{
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          dateOfBirth: new Date(2000, 0, 1),
          gender: "m",
          medicalTestProfileGuid: medicalTestProfileGuid ?? null,
          files: null,
          clientStreet: "",
          clientZip: "",
          clientCity: "",
          clientInsuranceType: undefined,
          clientInsuranceNumber: "",
          clientInsuranceCompanyGuid: undefined,
        }}
        schema={PatientSchema}
        submitText={isRequestMode ? t("Anfrage senden") : t("Termin buchen")}
        onSubmit={onSubmit}
      >
        <div className="flex flex-col gap-4 xl:hidden">
          <TextFormInput tabIndex={1} autoFocus name="firstName" label={t("Vorname")} />
          <TextFormInput tabIndex={2} name="lastName" label={t("Nachname")} />
          <DateFormInput tabIndex={3} name="dateOfBirth" label={t("Geburtsdatum")} type="date" />
          <SelectFormInput
            tabIndex={4}
            name="gender"
            label={t("Geschlecht")}
            options={[
              {
                label: t("Männlich"),
                value: "m",
              },
              {
                label: t("Weiblich"),
                value: "f",
              },
            ]}
          />
          <TextFormInput tabIndex={5} name="email" label={t("E-Mail")} />
          <TextFormInput tabIndex={6} name="phone" label={t("Telefon")} />
          {testProfileField}
          {hasVaccination ? (
            <>
              <StreetFormField label={t("Straße und Hausnummer")} />
              <TextFormInput tabIndex={8} name="clientZip" label={t("Postleitzahl")} />
              <TextFormInput tabIndex={9} name="clientCity" label={t("Stadt")} />
              <SelectFormInput
                tabIndex={10}
                name="clientInsuranceType"
                label={t("Versicherungsart ")}
                options={[
                  {
                    label: t("Gesetzlich versichert (GKV)"),
                    value: "GKV",
                  },
                  {
                    label: t("Privat versichert (PKV)"),
                    value: "PKV",
                  },
                ]}
              />
              <TextFormInput
                tabIndex={11}
                name="clientInsuranceNumber"
                label={t("Krankenversicherungsnummer (KVNR)")}
              />
              <ComboBoxFormInput
                tabIndex={12}
                name="clientInsuranceCompanyGuid"
                label={t("Krankenkasse")}
                options={insuranceCompanies}
              />
            </>
          ) : null}
          {add_documents_block}
        </div>

        <div className="hidden space-y-4 xl:block">
          <div className="grid grid-cols-1 gap-2 lg:grid-cols-2 lg:gap-4">
            <TextFormInput tabIndex={1} autoFocus name="firstName" label={t("Vorname")} />
            <TextFormInput tabIndex={2} name="lastName" label={t("Nachname")} />
            <DateFormInput tabIndex={3} name="dateOfBirth" label={t("Geburtsdatum")} type="date" />
            <SelectFormInput
              tabIndex={4}
              name="gender"
              label={t("Geschlecht")}
              options={[
                {
                  label: t("Männlich"),
                  value: "m",
                },
                {
                  label: t("Weiblich"),
                  value: "f",
                },
              ]}
            />
            <TextFormInput tabIndex={5} name="email" label={t("E-Mail")} />
            <TextFormInput tabIndex={6} name="phone" label={t("Telefon")} />
            {testProfileField}
            {hasVaccination ? (
              <>
                <StreetFormField label={t("Straße und Hausnummer")} />
                <TextFormInput tabIndex={9} name="clientZip" label={t("Postleitzahl")} />
                <TextFormInput tabIndex={10} name="clientCity" label={t("Stadt")} />
                <SelectFormInput
                  tabIndex={11}
                  name="clientInsuranceType"
                  label={t("Versicherungsart")}
                  options={[
                    {
                      label: t("Gesetzlich versichert (GKV)"),
                      value: "GKV",
                    },
                    {
                      label: t("Privat versichert (PKV)"),
                      value: "PKV",
                    },
                  ]}
                />
                <TextFormInput
                  tabIndex={12}
                  name="clientInsuranceNumber"
                  label={t("Krankenversicherungsnummer (KVNR)")}
                />
                <ComboBoxFormInput
                  tabIndex={13}
                  name="clientInsuranceCompanyGuid"
                  label={t("Krankenkasse")}
                  options={insuranceCompanies}
                />
              </>
            ) : null}
          </div>
          {add_documents_block}
        </div>

        <p>
          <Trans
            i18nKey="calendarAGB"
            components={[
              <a key="agb" target="_blank" href="/agb" className="text-blue-500 underline" />,
              <a key="datenschutz" target="_blank" href="/datenschutz" className="text-blue-500 underline" />,
            ]}
          />
        </p>
      </ReactHookForm>
    </div>
  );
}

function usePatientSchema(hasVaccination: boolean) {
  const { t } = useTranslation();

  return z.object({
    firstName: z.string().min(1, t("Vorname ist erforderlich")),
    lastName: z.string().min(1, t("Nachname ist erforderlich")),
    email: z.string().email(t("E-Mail ist ungültig")),
    phone: z.string().min(1, t("Telefon ist erforderlich")),
    dateOfBirth: z.date(),
    gender: z.enum(["m", "f"]),
    medicalTestProfileGuid: z.string().nullable(),
    files: z
      .any()
      .refine((val) => !val || (val instanceof FileList && val.length > 0), { message: "File is required" })
      .nullable(),
    clientStreet: hasVaccination ? z.string().min(1, t("Straße ist erforderlich")) : z.string().optional(),
    clientZip: hasVaccination ? z.string().min(1, t("Postleitzahl ist erforderlich")) : z.string().optional(),
    clientCity: hasVaccination ? z.string().min(1, t("Stadt ist erforderlich")) : z.string().optional(),
    clientInsuranceType: z.enum(["GKV", "PKV"]).optional(),
    // insurance_number is optional when insurance_type is PKV (private)
    clientInsuranceNumber: z.string().optional(),
    clientInsuranceCompanyGuid: z.string().optional(),
  });
}

export type TPatientSchema = z.infer<ReturnType<typeof usePatientSchema>>;

interface Props {
  onSubmit: (data: TPatientSchema) => void;
  medicalTestProfiles: MedicalTestProfile[];
  medicalTestProfileGuid?: string;
  appointmentType: PartnerAppointmentType;
  bookingError?: string | null;
}

function getZip(place: google.maps.places.PlaceResult) {
  return place.address_components?.find((c) => c.types.includes("postal_code"))?.long_name;
}

function getCity(place: google.maps.places.PlaceResult) {
  return place.address_components?.find((c) => c.types.includes("locality"))?.long_name;
}

function getStreet(place: google.maps.places.PlaceResult) {
  const street = place.address_components?.find((c) => c.types.includes("route"))?.long_name ?? "";
  const streetNumber = place.address_components?.find((c) => c.types.includes("street_number"))?.long_name ?? "";
  return `${street} ${streetNumber}`;
}

function Suggestions({
  predictions,
  onClick,
  getPlacePredictions,
}: {
  predictions: google.maps.places.AutocompletePrediction[];
  onClick: (prediction: google.maps.places.AutocompletePrediction) => void;
  getPlacePredictions: (options: google.maps.places.AutocompletionRequest) => void;
}) {
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const div = document.getElementById("suggestions");
      if (!div?.contains(event.target as Node)) {
        getPlacePredictions({ input: "" });
      }
    }
    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [getPlacePredictions]);

  return (
    <div className="absolute top-full z-10 space-y-2 border bg-white p-4 text-left" id="suggestions">
      {predictions.map((prediction) => (
        <button
          key={prediction.place_id}
          type="button"
          className="block w-full text-left"
          onClick={() => onClick(prediction)}
        >
          {prediction.description}
        </button>
      ))}
    </div>
  );
}

function Street({
  value,
  onChange,
  onAddressSelected,
  required,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  onAddressSelected: (street: string, zip: string, city: string) => void;
  required?: boolean;
  placeholder?: string;
}) {
  const { placePredictions, getPlacePredictions } = usePlacesService({
    apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string,
    options: { input: "", componentRestrictions: { country: "DE" }, types: ["address"] },
  });

  const handlePlaceSelection = async (prediction: google.maps.places.AutocompletePrediction) => {
    try {
      // Use the new Place API instead of PlacesService.getDetails()
      // Check if the new Place API is available
      if (typeof google === "undefined" || !google.maps?.places?.Place) {
        console.error("Google Maps Places API not loaded or Place class not available");
        return;
      }

      const place = new google.maps.places.Place({ id: prediction.place_id });
      await place.fetchFields({ fields: ["addressComponents"] });

      if (!place.addressComponents) return;

      // Convert Place.addressComponents to the format expected by helper functions
      // The new Place API uses addressComponents array with longText, shortText, and types
      const placeResult: google.maps.places.PlaceResult = {
        address_components: place.addressComponents.map((component) => ({
          long_name: component.longText ?? "",
          short_name: component.shortText ?? "",
          types: component.types ?? [],
        })),
      };

      const street = getStreet(placeResult) ?? "";
      const zip = getZip(placeResult) ?? "";
      const city = getCity(placeResult) ?? "";
      onChange(street);
      onAddressSelected(street, zip, city);
      getPlacePredictions({ input: "" });
    } catch (error) {
      reportError("Address lookup failed", error);
    }
  };

  return (
    <div className="relative">
      <Input
        type="text"
        value={value}
        onChange={(e) => {
          getPlacePredictions({ input: e.target.value });
          onChange(e.target.value);
        }}
        required={required}
        placeholder={placeholder}
        autoComplete="new-password"
        data-form-type="other"
      />
      {placePredictions?.length ? (
        <Suggestions
          getPlacePredictions={getPlacePredictions}
          predictions={placePredictions}
          onClick={handlePlaceSelection}
        />
      ) : null}
    </div>
  );
}

function StreetFormField({ label }: { label: string }) {
  const form = useFormContext();
  return (
    <FormField
      name="clientStreet"
      render={({ field }) => {
        return (
          <FormItem>
            <FormLabel>{label}</FormLabel>
            <FormControl>
              <Street
                value={field.value}
                onChange={(value) => {
                  field.onChange(value);
                  form.setValue("clientStreet", value);
                }}
                onAddressSelected={(street, zip, city) => {
                  form.setValue("clientStreet", street);
                  form.setValue("clientZip", zip);
                  form.setValue("clientCity", city);
                  field.onChange(street);
                }}
                required
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
